// EngineBroadcastRouter — pure helper that decides which inbound `ServerEvent`s
// should be replayed onto the local drawing engine.
//
// Why a free function (not a method on the engine):
//   - SessionProvider receives every server event through `transport.onEvent`;
//     only a small subset is "drawing material" (stroke.* / whiteboard.*).
//   - Echoes of our OWN strokes must be skipped, otherwise the local engine
//     would render them twice (once via `engine.startStroke / addPoints /
//     endStroke` driven by pointer input, once via the broadcast round-trip).
//   - Phase 5 reuses the same `applyRemoteStroke` entry point for
//     `room.welcome.deltaEvents` and `room.resume-ok.events`. Keeping the
//     filter pure means snapshot replay can use it too.
//
// (architecture.md §16 / §19.6, protocol-events.md §0.1 broadcast variants)

import type { ServerEvent } from '@oekaki/protocol';

/**
 * Minimal engine surface this router cares about. The real
 * `CanvasDrawingEngine` satisfies it; tests substitute a spy.
 */
export interface RemoteStrokeEngine {
  applyRemoteStroke(events: ReadonlyArray<unknown>): void;
  /**
   * Replace the opaque canvas background color. Driven by the
   * `whiteboard.background-set` broadcast (owner canvas bg color, A-2
   * transport; docs/_wip/canvas-background-color-2026-06-20.md §4.3).
   * The protocol payload carries `rgb` only — alpha is fixed opaque (255)
   * at the apply boundary here, NOT in the wire format.
   */
  setBackground(next: { rgb: number; alpha: number }): void;
}

/**
 * Server event kinds that the drawing engine can replay. We accept the
 * loose shape (`unknown`) inside `applyRemoteStroke`, but at this layer we
 * still gate on the discriminator literal to avoid handing the engine
 * unrelated payloads (chat, member, system, …).
 */
const ENGINE_REPLAYABLE_TYPES = new Set<ServerEvent['t']>([
  'stroke.start',
  'stroke.segment',
  'stroke.end',
  'whiteboard.clear',
  // `whiteboard.imagepaste` broadcast lands here. Phase 7 Task 7-8a wired
  // the engine's commit-on-broadcast handler — the engine fetches the
  // presigned URL, decodes, stamps `Layer.main`, and clears any preview
  // overlay. See `EngineBroadcastRouter` echo-suppression comment for why
  // imagepaste is exempt from the self-uid filter.
  'whiteboard.imagepaste',
  // `whiteboard.region` (範囲の移動・コピー): 画像を fetch → decode → 消す/置く。
  // 自分の echo も適用する (下の SKIP_ECHO_SUPPRESSION)。
  'whiteboard.region',
  // `whiteboard.background-set` broadcast (A-2 transport). Applied via the
  // engine's `setBackground` (NOT `applyRemoteStroke`); see the apply switch
  // below. Listed here so the gate doesn't drop it before that branch.
  'whiteboard.background-set',
]);

/**
 * Event kinds for which echo-suppression by self-uid is NOT applied. The
 * receiver wants to see its own broadcast for these — typically because
 * the broadcast is the commit signal for some local pending state.
 *
 * `whiteboard.imagepaste` (Phase 7 Task 7-8a):
 *   The originator keeps a local preview overlay between confirm-click
 *   and broadcast-arrival. The broadcast is the cue to decode the URL
 *   and stamp `Layer.main` (the preview is display-only — never written
 *   into the committed layer). Suppressing the self echo would leave the
 *   preview hanging forever.
 *
 * `whiteboard.clear` (bug fix 2026-05-06):
 *   We do NOT clear locally at button-click time (= optimistic). The send
 *   path (`useCases.sendClear`) only ships `{ t: 'whiteboard.clear' }` to
 *   the server, which gates on `Perm.CLEAR || room.clearOpen` and may
 *   reject. Broadcast-receipt is therefore the single source of truth for
 *   the actual clear; if we suppressed the self echo, the originator's
 *   own canvas would stay dirty while everyone else's cleared. Mirrors
 *   the imagepaste pattern: server-confirmed, then locally applied.
 */
const SKIP_ECHO_SUPPRESSION: ReadonlySet<ServerEvent['t']> = new Set<ServerEvent['t']>([
  'whiteboard.imagepaste',
  // `whiteboard.region`: 確定してもローカルでは先に適用しない (server が却下しうる)。
  // 発信者も自分の echo を受けて適用する (画像貼り付けと同じ)。
  'whiteboard.region',
  'whiteboard.clear',
  // `whiteboard.background-set` (A-2 transport, 2026-06-20): same rationale as
  // whiteboard.clear. The send path (`useCases.sendBackgroundSet`) ships only
  // `{ t, rgb }` with no optimistic local apply; the server gates on
  // `hasPerm(Perm.CLEAR)` and may reject. Broadcast-receipt is the single
  // source of truth, so the originator MUST receive its own echo to apply.
  'whiteboard.background-set',
]);

/**
 * Decide whether `event` is a drawing broadcast that should be applied to
 * the local engine, **after** echo suppression for our own uid (where
 * applicable — see `SKIP_ECHO_SUPPRESSION`).
 *
 * Returns `true` if it was forwarded (engine.applyRemoteStroke called),
 * `false` otherwise. The boolean is for tests; the Provider doesn't need it.
 */
export const routeBroadcastToEngine = (
  event: ServerEvent,
  selfUid: string | null,
  engine: RemoteStrokeEngine | null,
): boolean => {
  if (engine === null) return false;
  if (!ENGINE_REPLAYABLE_TYPES.has(event.t)) return false;

  // Echo suppression: stroke.* broadcasts carry the originator's `uid`
  // (server-stamped per protocol-events.md §0.1). Our own strokes are
  // already drawn locally via `engine.startStroke / addPoints / endStroke`
  // from pointer events; replaying the echo would commit a second copy.
  // `whiteboard.imagepaste` and `whiteboard.clear` are exceptions — they
  // are NOT applied locally before broadcast (server may reject), so the
  // originator must receive its own echo to commit. See
  // `SKIP_ECHO_SUPPRESSION` comment.
  if (
    selfUid !== null &&
    hasUid(event) &&
    event.uid === selfUid &&
    !SKIP_ECHO_SUPPRESSION.has(event.t)
  ) {
    return false;
  }

  // `whiteboard.background-set` is applied through the dedicated
  // `setBackground` entry point, NOT `applyRemoteStroke` (which only knows
  // stroke.* / clear / imagepaste schemas). The wire payload carries `rgb`
  // only; alpha is fixed opaque (255) here at the apply boundary, matching
  // the server's "owner can't make the backdrop translucent" rule.
  if (event.t === 'whiteboard.background-set') {
    engine.setBackground({ rgb: event.rgb, alpha: 255 });
    return true;
  }

  engine.applyRemoteStroke([event]);
  return true;
};

const hasUid = (event: ServerEvent): event is ServerEvent & { uid: string } =>
  'uid' in event && typeof (event as { uid: unknown }).uid === 'string';

/**
 * Type guard for the `whiteboard.background-set` delta entry. The replay
 * buffers (`room.welcome.deltaEvents` / `room.resume-ok.events`) are typed
 * as `unknown[]` at the Provider boundary, so we narrow defensively on the
 * discriminator + `rgb` shape rather than trusting the static type.
 */
const isBackgroundSetEvent = (
  event: unknown,
): event is { t: 'whiteboard.background-set'; rgb: number } =>
  typeof event === 'object' &&
  event !== null &&
  (event as { t?: unknown }).t === 'whiteboard.background-set' &&
  typeof (event as { rgb?: unknown }).rgb === 'number';

/**
 * Replay a *batch* of delta events onto the engine, used by the late-join /
 * resume catch-up paths (`room.welcome.deltaEvents`, `room.resume-ok.events`
 * — protocol-events.md §2.7-2.10). This is the buffered counterpart to the
 * live single-event {@link routeBroadcastToEngine}.
 *
 * Why this exists (bug 2026-06-20): the live path has a dedicated
 * `whiteboard.background-set` → `setBackground` branch, but the catch-up
 * path used to hand the *whole* buffer straight to `engine.applyRemoteStroke`,
 * which only understands stroke/clear/imagepaste schemas and silently drops
 * `background-set`. A client that joined *before* the next snapshot captured
 * the new bg colour would therefore stay white. We split the buffer here so
 * background-set is routed through `setBackground` just like the live path.
 *
 * Behaviour:
 *   1. Partition `events` into `background-set` vs everything else.
 *   2. Apply the rest in a SINGLE `applyRemoteStroke(rest)` call — preserving
 *      the existing batched-replay perf for stroke/clear/imagepaste.
 *   3. Apply the LAST background-set's rgb via `setBackground({ rgb, alpha: 255 })`
 *      (last-wins). Background is an independent property composed beneath all
 *      layers; its apply order relative to strokes does not affect the final
 *      raster, so for catch-up only the final bg needs to be correct.
 *
 * @returns the final applied background rgb, or `null` if the batch contained
 *   no background-set. The caller uses this to drive `notifyBackground` so the
 *   ■ swatch reflects the delta-derived colour (which is newer than any
 *   snapshot meta.background).
 */
export const replayDeltaEventsToEngine = (
  engine: RemoteStrokeEngine,
  events: ReadonlyArray<unknown>,
): number | null => {
  let lastBgRgb: number | null = null;
  const rest: unknown[] = [];
  for (const ev of events) {
    if (isBackgroundSetEvent(ev)) {
      // last-wins: keep overwriting so the final entry's colour is applied.
      lastBgRgb = ev.rgb;
      // Explicitly exclude bg-set from the stroke batch — `applyRemoteStroke`
      // would silently ignore it (the original bug), so we never hand it over.
      continue;
    }
    rest.push(ev);
  }
  if (rest.length > 0) {
    engine.applyRemoteStroke(rest);
  }
  if (lastBgRgb !== null) {
    engine.setBackground({ rgb: lastBgRgb, alpha: 255 });
  }
  return lastBgRgb;
};
